import type { Dicom } from '@/models/dicom';

/**
 * View-classification taxonomy for the viewer's View Filter and View Groups
 * (AP-441). FE-static for now, intentionally shaped like a future
 * `GET /view-taxonomy` payload so the source can be swapped without touching
 * call sites. Group definitions and aliases are seeded from the clinical
 * "View Classification Grouping Filters" spreadsheet.
 *
 * Matching is on a dicom's canonical `view` label (e.g. "A4C", "PLAX Standard"),
 * which the encephalon classifier already emits in that fine-grained form.
 */

export type ViewTier = 'Essential' | 'Supportive' | 'Optional';

export const TIER_ORDER: readonly ViewTier[] = [
  'Essential',
  'Supportive',
  'Optional',
];

/** Selectable pseudo-option for dicoms that have no view prediction. */
export const UNCLASSIFIED_VIEW = 'Unclassified';

export interface ViewGroup {
  id: string;
  displayName: string;
  /** View labels per clinical tier, in priority order. */
  tiers: Record<ViewTier, string[]>;
}

/**
 * Synonyms that collapse to one canonical key for matching. The classifier
 * emits "PLAX Pericardial"; the spreadsheet taxonomy spells it "PLAX
 * Paracardial". Treat them as the same view. Keyed by normalized-lowercase.
 * NOTE: flagged to clinical — one of the two spellings is a typo.
 */
const SYNONYMS: Record<string, string> = {
  'plax paracardial': 'plax pericardial',
};

/**
 * Abbreviation → expanded search text, so the View Filter matches full names
 * AND abbreviations (ticket: "Apical Four Chamber" / "Apical 4 Chamber" / "A4C").
 * Both the word and digit forms are included. Keyed by normalized-lowercase token.
 */
const ABBREV_EXPANSIONS: Record<string, string> = {
  a2c: 'apical two 2 chamber',
  a3c: 'apical three 3 chamber',
  a4c: 'apical four 4 chamber',
  a5c: 'apical five 5 chamber',
  plax: 'parasternal long axis',
  psax: 'parasternal short axis',
  psaxa: 'parasternal short axis aorta',
  rvit: 'right ventricular inflow tract',
  sub: 'subcostal',
  subc: 'subcostal',
  supa: 'suprasternal',
  ivc: 'inferior vena cava',
  ias: 'interatrial septum',
  av: 'aortic valve',
  mv: 'mitral valve',
  tv: 'tricuspid valve',
  pv: 'pulmonary valve',
  lvot: 'left ventricular outflow tract',
  lv: 'left ventricle',
  rv: 'right ventricle',
  la: 'left atrium',
  ra: 'right atrium',
  aa: 'ascending aorta',
  ao: 'aorta',
  cw: 'continuous wave doppler',
  pw: 'pulsed wave doppler',
  td: 'tissue doppler',
  desc: 'descending',
};

/**
 * Clinical view groups from the "View Classification Grouping Filters"
 * spreadsheet. Each group lists the views a reader focuses on for that
 * pathology, by clinical tier (Essential → Supportive → Optional) in priority
 * order. Ordered as in the spreadsheet so the View Group dropdown reads
 * clinically. View labels use the spreadsheet spelling; SYNONYMS reconcile any
 * classifier spelling differences (e.g. Paracardial/Pericardial).
 */
export const VIEW_GROUPS: readonly ViewGroup[] = [
  {
    id: 'lv-function',
    displayName: 'LV Function / Cardiomyopathy',
    tiers: {
      Essential: [
        'PLAX Standard',
        'A4C',
        'A4C Zoomed LV',
        'A2C',
        'A3C',
        'PSAX Mitral',
        'PSAX Papillary',
        'PSAX Apex',
      ],
      Supportive: ['Long Axis_M mode through LV'],
      Optional: [],
    },
  },
  {
    id: 'diastolic-function',
    displayName: 'Diastolic Function / HFpEF',
    tiers: {
      Essential: [
        'A4C',
        'A2C',
        'PLAX Standard',
        'Apical_PW Doppler of MV',
        'Apical_TD MV Septal Annulus',
        'Apical_CW Doppler of TV Inflow',
      ],
      Supportive: [
        'A4C Color on Tricuspid',
        'A4C Color Pulmonary Vein',
        'Long Axis_M-mode through AA and LA',
        'Long Axis_M mode through LV',
      ],
      Optional: [],
    },
  },
  {
    id: 'mitral-regurgitation',
    displayName: 'Mitral Regurgitation',
    tiers: {
      Essential: [
        'A4C',
        'A4C Zoomed Mitral',
        'A2C',
        'A2C Zoomed Mitral',
        'PLAX Standard',
        'PLAX Mitral Cusps',
        'A4C Color on MV',
        'A2C Color on MV',
        'PLAX Color on MV',
      ],
      Supportive: [
        'Apical_CW Doppler of MV Inflow',
        'Apical_PW Doppler of MV',
        'Apical_TD MV Septal Annulus',
      ],
      Optional: ['PSAX Mitral', 'A3C Color on MV'],
    },
  },
  {
    id: 'aortic-stenosis',
    displayName: 'Aortic Stenosis',
    tiers: {
      Essential: [
        'PLAX Standard',
        'PLAX Aortic Cusps',
        'PSAX Pulmonary',
        'A5C',
        'A5C Zoomed Aorta',
        'PLAX Color AV',
        'A5C Color on AV',
        'Apical_CW Doppler of AV Outflow',
        'Apical_PW Doppler of LVOT',
      ],
      Supportive: [
        'Long Axis_M-mode through AA and LA',
        'Long Axis_M mode through LV',
      ],
      Optional: ['A4C', 'Pedoff'],
    },
  },
  {
    id: 'right-heart-ph',
    displayName: 'Right Heart / Pulmonary Hypertension',
    tiers: {
      Essential: [
        'A4C Zoomed RV',
        'A4C',
        'RVIT',
        'PSAX Pulmonary',
        'SUB IVC',
        'A4C Color on Tricuspid',
        'Apical_CW Doppler of TV Inflow',
      ],
      Supportive: [
        'Short Axis_PW Doppler of PV Outflow',
        'Subcostal Standard',
        'PSAX Color on Pulmonary',
        'Long Axis_CW Doppler of TV Inflow',
        'Subcostal_IVC Valsalva',
      ],
      Optional: [],
    },
  },
  {
    id: 'aortic-regurgitation',
    displayName: 'Aortic Regurgitation',
    tiers: {
      Essential: [
        'PLAX Standard',
        'PLAX Aortic Cusps',
        'A3C',
        'PLAX Color AV',
        'A3C Color on AV',
        'A5C Color on AV',
        'Apical_CW Doppler of AV Outflow',
      ],
      Supportive: [
        'A4C',
        'Long Axis_M-mode through AA and LA',
        'Suprasternal_PW Doppler of Desc Ao',
        'SUPA Color Ascending Ao',
      ],
      Optional: ['SUPA Color Decscending Ao', 'PSAXA Zoomed Aorta'],
    },
  },
  {
    id: 'pericardial-effusion',
    displayName: 'Pericardial Effusion / Tamponade',
    tiers: {
      Essential: [
        'PLAX Standard',
        'PLAX Paracardial',
        'PSAX Zoomed Out',
        'A4C',
        'Subcostal Standard',
        'SUB IVC',
        'Apical_PW Doppler of MV',
        'Apical_CW Doppler of TV Inflow',
      ],
      Supportive: ['Subcostal_IVC Valsalva', 'A2C'],
      Optional: ['PSAX Apex'],
    },
  },
];

const collapse = (s: string): string => s.trim().replace(/\s+/g, ' ');

/** Normalized, synonym-resolved key for equality comparisons. */
export function viewKey(label: string): string {
  const normalized = collapse(label).toLowerCase();
  return SYNONYMS[normalized] ?? normalized;
}

/** Lowercased label plus abbreviation expansions, for substring search. */
export function viewSearchText(label: string): string {
  const base = collapse(label).toLowerCase();
  const expanded = base
    .split(' ')
    .map((token) => ABBREV_EXPANSIONS[token] ?? '')
    .filter(Boolean)
    .join(' ');
  return expanded ? `${base} ${expanded}` : base;
}

/**
 * True when every whitespace-separated token of `query` appears in the view's
 * search text. Forgiving substring matching across full names + abbreviations.
 */
export function viewMatchesQuery(label: string, query: string): boolean {
  const q = collapse(query).toLowerCase();
  if (!q) return true;
  const text = viewSearchText(label);
  return q.split(' ').every((token) => text.includes(token));
}

export function getViewGroup(id: string | null): ViewGroup | null {
  if (!id) return null;
  return VIEW_GROUPS.find((g) => g.id === id) ?? null;
}

/**
 * Priority rank of a view within a group (0 = highest), or null if the view is
 * not a member. Tiers are ordered Essential → Supportive → Optional, and views
 * keep their listed order within a tier.
 */
export function groupRank(group: ViewGroup, label: string): number | null {
  const key = viewKey(label);
  // Flatten tiers in priority order; the index is the rank (0 = highest).
  const ordered = TIER_ORDER.flatMap((tier) => group.tiers[tier]);
  const idx = ordered.findIndex((member) => viewKey(member) === key);
  return idx === -1 ? null : idx;
}

/** Distinct view labels present in the study, for the View Filter options. */
export function listStudyViews(dicoms: Dicom[]): string[] {
  const byKey = new Map<string, string>();
  let hasUnclassified = false;
  dicoms.forEach((d) => {
    const label = d.viewPrediction?.view?.trim();
    if (label) {
      if (!byKey.has(viewKey(label))) byKey.set(viewKey(label), label);
    } else {
      hasUnclassified = true;
    }
  });
  const views = [...byKey.values()].sort((a, b) => a.localeCompare(b));
  return hasUnclassified ? [...views, UNCLASSIFIED_VIEW] : views;
}

/**
 * Apply the View Filter (multi-select) and View Group filters, AND-combined,
 * and prioritize by clinical tier when a group is active.
 *
 * - `selectedViews` empty + no group ⇒ returns `dicoms` unchanged (show all).
 * - Unclassified dicoms (no `viewPrediction`) are hidden whenever a filter is
 *   active, unless the reader explicitly selects "Unclassified" (and no group is
 *   active, since unclassified views belong to no clinical group).
 * - With a group active, matching dicoms are stable-sorted by tier rank; ties
 *   keep their incoming order. Callers should pass dicoms pre-sorted by index.
 */
export function filterAndPrioritizeDicoms(
  dicoms: Dicom[],
  selectedViews: string[],
  selectedGroupId: string | null
): Dicom[] {
  const group = getViewGroup(selectedGroupId);
  const selectedKeys = new Set(selectedViews.map(viewKey));
  const hasViewFilter = selectedKeys.size > 0;
  const wantUnclassified = selectedKeys.has(viewKey(UNCLASSIFIED_VIEW));

  if (!hasViewFilter && !group) return dicoms;

  const passing = dicoms.filter((d) => {
    const label = d.viewPrediction?.view;
    if (!label) return wantUnclassified && !group;
    const key = viewKey(label);
    if (hasViewFilter && !selectedKeys.has(key)) return false;
    if (group && groupRank(group, label) === null) return false;
    return true;
  });

  if (!group) return passing;

  return passing
    .map((d, i) => ({
      d,
      i,
      rank: groupRank(group, d.viewPrediction!.view) ?? Number.MAX_SAFE_INTEGER,
    }))
    .sort((a, b) => a.rank - b.rank || a.i - b.i)
    .map((entry) => entry.d);
}
